import React, { useState, useEffect } from 'react';

import { useRentCalculator } from './context';

import CalculatorLayout from './components/layout';

import { LayoutWrapper } from 'app/components/Layout/Wrapper/styled-components';

import Stepper from './components/stepper';

import Image from 'app/components/Image';

import { maskEmail } from './utils/email';
import { formatDataForSubmission } from './utils/formatData';
import { floors, size, squareMeters } from './utils/options';

// styled
import { ImageWrapper, ImageContainer } from './styled/image';
import { Select, SelectWrapper } from './styled/select';
import { EmailHiddenText, EmailTextHeader } from './styled/email';
import {
  AmenitiesContainer,
  AmenitiesContent,
  AmenityName,
  AmenityWrapper,
} from './styled/amenity';
import { Label } from './styled/label';
import { RoomDetails, RoomFormRow, RoomSection } from './styled/room';
import { Input } from './styled/input';
import { FormGroup } from './styled/form';
import { PersonalInformation, CityName, PropertyInformation } from './styled/information';
import {
  SquareMeterInput,
  SquareMeterInputContainer,
  SquareMeterLabel,
} from './styled/squareMeterInput';

const RentCalculator = () => {
  const { setCityData, getCityData, cityData, submitRentCalculation, isFeedback, error } =
    useRentCalculator();
  const [selectedCity, setSelectedCity] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [roomsData, setRoomsData] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    district: '',
    rooms: '1',
    bathrooms: '1',
    floor: '0-3',
    squareMeters: '',
    privacyAccepted: false,
  });

  const [amenities, setAmenities] = useState({
    has_salon: false,
    has_terrace: false,
    has_balcony: false,
    has_natural_light: false,
    has_ac: false,
    has_elevator: false,
  });

  const toggleAmenity = (amenity) => {
    setAmenities((prev) => ({
      ...prev,
      [amenity]: !prev[amenity],
    }));
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoomDataChange = (roomIndex, field, value) => {
    setRoomsData((prev) => {
      const newData = [...prev];
      // Si se está seleccionando terraza o balcón, asegurarse que el otro esté deseleccionado
      if (field === 'has_terrace' && value === true) {
        newData[roomIndex] = {
          ...newData[roomIndex],
          has_terrace: true,
          has_balcony: false,
        };
      } else if (field === 'has_balcony' && value === true) {
        newData[roomIndex] = {
          ...newData[roomIndex],
          has_balcony: true,
          has_terrace: false,
        };
      } else {
        newData[roomIndex] = {
          ...newData[roomIndex],
          [field]: value,
        };
      }
      return newData;
    });
  };

  useEffect(() => {
    const initialRoomsData = Array(Number(formData.rooms)).fill({
      size_range: '5-10',
      is_suite: false,
      has_terrace: false,
      has_balcony: false,
      exposure: 'exterior',
      has_ac: false,
      has_ventilator: false,
    });
    setRoomsData(initialRoomsData);
  }, [formData.rooms]);

  useEffect(() => {
    const data = getCityData(selectedCity);
    if (data) {
      setCityData(data);
      const districtKeys = Object.keys(data.districts);
      setDistricts(districtKeys);
      if (districtKeys.length > 0) {
        // Auto-select the first district
        setFormData((prevFormData) => ({
          ...prevFormData,
          district: data.districts[districtKeys[0]].district, // Set the first district as the selected one
        }));
      }
    }
  }, [selectedCity]);

  const handleComplete = () => {
    const formattedData = formatDataForSubmission({ formData, selectedCity, roomsData, amenities });
    submitRentCalculation(formattedData);
  };

  const steps = [
    {
      title: 'Ubicación',
      content: (
        <PersonalInformation>
          <Label bold>Ciudad de la propiedad</Label>
          <ImageContainer>
            <ImageWrapper
              isSelected={selectedCity === 'barcelona'}
              onClick={() => setSelectedCity('barcelona')}
            >
              <Image src="/assets/images/barcelona-icon.svg" alt="Barcelona" />
              <CityName isSelected={selectedCity === 'barcelona'}>Barcelona</CityName>
            </ImageWrapper>

            <ImageWrapper
              isSelected={selectedCity === 'madrid'}
              onClick={() => setSelectedCity('madrid')}
            >
              <Image src="/assets/images/madrid-icon.svg" alt="Madrid" />
              <CityName isSelected={selectedCity === 'madrid'}>Madrid</CityName>
            </ImageWrapper>

            <ImageWrapper
              isSelected={selectedCity === 'valencia'}
              onClick={() => setSelectedCity('valencia')}
            >
              <Image src="/assets/images/valencia-icon.svg" alt="Valencia" />
              <CityName isSelected={selectedCity === 'valencia'}>Valencia</CityName>
            </ImageWrapper>
          </ImageContainer>
          <SelectWrapper>
            <Label bold>Distrito de la propiedad</Label>
            <Select
              disabled={!selectedCity}
              value={formData.district}
              onChange={(e) => handleInputChange('district', e.target.value)}
              fullWidth
            >
              {/* Show a placeholder when disabled */}
              {!selectedCity && (
                <option value="" disabled>
                  Elige un distrito
                </option>
              )}
              {districts.map((districtKey) => {
                const districtData = cityData.districts[districtKey];
                return (
                  <option key={districtKey} value={districtData.district}>
                    {districtData.district}
                  </option>
                );
              })}
            </Select>
          </SelectWrapper>
        </PersonalInformation>
      ),
    },
    {
      title: 'Información de la propiedad',
      content: (
        <>
          <PropertyInformation>
            <FormGroup>
              <Label>Metros cuadrados de la propiedad</Label>
              <SquareMeterInputContainer>
                <SquareMeterInput
                  type="number"
                  placeholder="m²"
                  value={formData.squareMeters}
                  onChange={(e) => handleInputChange('squareMeters', e.target.value)}
                />
                {formData.squareMeters && <SquareMeterLabel>m²</SquareMeterLabel>}
              </SquareMeterInputContainer>
            </FormGroup>
            <FormGroup>
              <Label>Número de habitaciones en la propiedad</Label>
              <Select
                value={formData.rooms}
                onChange={(e) => handleInputChange('rooms', e.target.value)}
              >
                {size.map((s) => {
                  return (
                    <option value={s.value} key={s.value}>
                      {s.displayName}
                    </option>
                  );
                })}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Número de baños en la propiedad</Label>
              <Select
                value={formData.bathrooms}
                onChange={(e) => handleInputChange('bathrooms', e.target.value)}
              >
                {size.map((s) => {
                  return (
                    <option value={s.value} key={s.value}>
                      {s.displayName}
                    </option>
                  );
                })}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Altura del piso</Label>
              <Select
                value={formData.floor}
                onChange={(e) => handleInputChange('floor', e.target.value)}
              >
                {floors.map((floor) => {
                  return (
                    <option value={floor.value} key={floor.value}>
                      {floor.displayName}
                    </option>
                  );
                })}
              </Select>
            </FormGroup>
          </PropertyInformation>

          <AmenitiesContainer>
            <Label bold>Indica qué características tiene la propiedad</Label>
            <AmenitiesContent>
              <AmenityWrapper
                isSelected={amenities.has_salon}
                onClick={() => toggleAmenity('has_salon')}
              >
                <Image src="/assets/images/sofa-calculator-icon.svg" alt="Salón" />
                <AmenityName>Salón</AmenityName>
              </AmenityWrapper>

              <AmenityWrapper
                isSelected={amenities.has_terrace}
                onClick={() => toggleAmenity('has_terrace')}
              >
                <Image src="/assets/images/terrace-calculator-icon.svg" alt="Terraza" />
                <AmenityName>Terraza</AmenityName>
              </AmenityWrapper>

              <AmenityWrapper
                isSelected={amenities.has_balcony}
                onClick={() => toggleAmenity('has_balcony')}
              >
                <Image src="/assets/images/balcony-calculator-icon.svg" alt="Balcón" />
                <AmenityName>Balcón</AmenityName>
              </AmenityWrapper>

              <AmenityWrapper
                isSelected={amenities.has_natural_light}
                onClick={() => toggleAmenity('has_natural_light')}
              >
                <Image src="/assets/images/umbrella-calculator-icon.svg" alt="Luz natural" />
                <AmenityName>Luz natural</AmenityName>
              </AmenityWrapper>

              <AmenityWrapper isSelected={amenities.has_ac} onClick={() => toggleAmenity('has_ac')}>
                <Image src="/assets/images/ac-calculator-icon.svg" alt="Aire acondicionado" />
                <AmenityName>AC</AmenityName>
              </AmenityWrapper>

              <AmenityWrapper
                isSelected={amenities.has_elevator}
                onClick={() => toggleAmenity('has_elevator')}
              >
                <Image src="/assets/images/elevator-calculator-icon.svg" alt="Ascensor" />
                <AmenityName>Ascensor</AmenityName>
              </AmenityWrapper>
            </AmenitiesContent>
          </AmenitiesContainer>
        </>
      ),
    },
    {
      title: 'Información de las habitaciones',
      content: (
        <RoomDetails>
          {Array.from({ length: Number(formData.rooms) }).map((_, index) => (
            <RoomSection key={index}>
              <Label bold>Habitación #{index + 1}</Label>
              <RoomFormRow>
                <FormGroup>
                  <Label>Tamaño de la habitación</Label>
                  <Select
                    value={roomsData[index]?.size_range}
                    onChange={(e) => handleRoomDataChange(index, 'size_range', e.target.value)}
                  >
                    {squareMeters.map((sqm) => {
                      return (
                        <option value={sqm.value} key={sqm.value}>
                          {sqm.displayName}
                        </option>
                      );
                    })}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Exposición</Label>
                  <Select
                    value={roomsData[index]?.exposure}
                    onChange={(e) => handleRoomDataChange(index, 'exposure', e.target.value)}
                  >
                    <option value="exterior">Exterior</option>
                    <option value="interior">Interior</option>
                  </Select>
                </FormGroup>
              </RoomFormRow>

              <AmenitiesContainer>
                <Label bold>Indica qué características tiene la habitación #{index + 1}</Label>
                <AmenitiesContent wide>
                  <AmenityWrapper
                    wide
                    isSelected={roomsData[index]?.is_suite}
                    onClick={() =>
                      handleRoomDataChange(index, 'is_suite', !roomsData[index]?.is_suite)
                    }
                  >
                    <Image src="/assets/images/bathroom-calculator-icon.svg" alt="Suite" />
                    <AmenityName>Baño privado</AmenityName>
                  </AmenityWrapper>

                  <AmenityWrapper
                    wide
                    isSelected={roomsData[index]?.has_terrace}
                    onClick={() =>
                      handleRoomDataChange(index, 'has_terrace', !roomsData[index]?.has_terrace)
                    }
                  >
                    <Image src="/assets/images/terrace-calculator-icon.svg" alt="Terraza" />
                    <AmenityName>Terraza</AmenityName>
                  </AmenityWrapper>

                  <AmenityWrapper
                    wide
                    isSelected={roomsData[index]?.has_balcony}
                    onClick={() =>
                      handleRoomDataChange(index, 'has_balcony', !roomsData[index]?.has_balcony)
                    }
                  >
                    <Image src="/assets/images/balcony-calculator-icon.svg" alt="Balcón" />
                    <AmenityName>Balcón</AmenityName>
                  </AmenityWrapper>

                  <AmenityWrapper
                    wide
                    isSelected={roomsData[index]?.has_ac}
                    onClick={() => handleRoomDataChange(index, 'has_ac', !roomsData[index]?.has_ac)}
                  >
                    <Image src="/assets/images/ac-calculator-icon.svg" alt="Aire acondicionado" />
                    <AmenityName>A/C</AmenityName>
                  </AmenityWrapper>
                </AmenitiesContent>
              </AmenitiesContainer>
            </RoomSection>
          ))}
        </RoomDetails>
      ),
    },
    {
      title: 'Datos personales',
      content: (
        <PersonalInformation>
          <Input
            fullWidth
            type="text"
            placeholder="Nombre*"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
          <Input
            fullWidth
            type="email"
            placeholder="Correo electrónico de contacto*"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
          />
          <Input
            fullWidth
            type="tel"
            placeholder="Teléfono de contacto*"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />
        </PersonalInformation>
      ),
    },
    {
      title: 'Cálculo',
      content: (
        <div>
          <EmailTextHeader>Recibe tu cálculo por email</EmailTextHeader>
          <EmailHiddenText>({maskEmail(formData.email)})</EmailHiddenText>
        </div>
      ),
    },
  ];

  return (
    <LayoutWrapper>
      <CalculatorLayout feedback={isFeedback}>
        <Stepper
          steps={steps}
          formData={formData}
          roomsData={roomsData}
          selectedCity={selectedCity}
          amenities={amenities}
          onInputChange={handleInputChange}
          onComplete={handleComplete}
          isFeedback={isFeedback}
          error={error}
        />
      </CalculatorLayout>
    </LayoutWrapper>
  );
};

export default RentCalculator;
